import { useTranslation } from 'react-i18next';

import type { ObjectiveStatus } from '@/utils/objectiveStatus';

import i18n from '@/plugins/i18n';
import { formatPercent } from '@/utils/numberFormat';
import { OBJECTIVE_STATUS_LABEL_KEYS } from '@/utils/objectiveStatus';

/** Start and end of the gradient the overall bar and its big number share. */
const PROGRESS_GRADIENT = 'bg-gradient-to-r from-obj-accent-deep to-obj-accent';

/** The dot beside the state speaks in the colour the objective's card gives the same state. */
const STATUS_DOT_CLASS_NAMES: Record<ObjectiveStatus, string> = {
    completed: 'bg-primary',
    onTrack: 'bg-obj-accent',
    atRisk: 'bg-warning',
    overdue: 'bg-error',
};

interface ObjectiveOverallProgressProps {
    progress: number;
    status: ObjectiveStatus;
}

/**
 * "Progression globale": a bare block on the page background in the prototype, not a card —
 * label, full-width gradient bar, and the huge gradient number beside it.
 */
export default function ObjectiveOverallProgress({
    progress,
    status,
}: ObjectiveOverallProgressProps) {
    const { t } = useTranslation();

    return (
        <div
            className={'flex flex-col gap-3'}
            data-testid={'objective-detail-overall-progress'}
        >
            <div className={'obj-overall-progress-header'}>
                <h2 className={'text-obj-gray text-[20px] font-medium'}>
                    {t('objectives.detail.overallProgress')}
                </h2>

                <div className={'flex items-center gap-1.5'}>
                    <div
                        className={`h-2.5 w-2.5 rounded-full ${STATUS_DOT_CLASS_NAMES[status]}`}
                        aria-hidden
                    />

                    <span className={'text-obj-gray text-[14px] font-medium'}>
                        {t(OBJECTIVE_STATUS_LABEL_KEYS[status])}
                    </span>
                </div>
            </div>

            <div className={'flex items-center gap-4'}>
                <div
                    className={
                        'obj-overall-progress-track flex-1 overflow-hidden rounded-[102px] bg-white/45 shadow-sm backdrop-blur-[2px]'
                    }
                >
                    <div
                        className={`h-full rounded-full ${PROGRESS_GRADIENT} shadow-sm transition-all duration-500`}
                        style={{ width: `${progress}%` }}
                        role={'progressbar'}
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={t('objectives.card.progressAria', { value: progress })}
                    />
                </div>

                <span
                    className={`obj-overall-progress-value shrink-0 ${PROGRESS_GRADIENT} bg-clip-text leading-none font-medium text-transparent`}
                >
                    {formatPercent(progress, i18n.language)}
                </span>
            </div>
        </div>
    );
}
